import React, { useEffect, useMemo, useState } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Truck,
  Search,
  Plus,
  AlertCircle,
  CheckCircle2,
  X
} from 'lucide-react';

/**
 * PROVEEDORES (Layout estándar similar a Clientes)
 * - Sin popouts: mensajes/confirmaciones inline.
 * - Mantiene funcionalidades: ver, editar, whatsapp, archivar/reactivar.
 */

export default function Proveedores({ onNavigate }) {
  const { profile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [proveedores, setProveedores] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [verArchivados, setVerArchivados] = useState(false);

  // Mensajería inline
  const [uiMsg, setUiMsg] = useState(null); // { type: 'error'|'success'|'info', title, message }
  // (Reservado) confirmaciones para futuras acciones.

  const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });

  const showMsg = (type, title, message) => {
    setUiMsg({ type, title, message });
    // auto-hide suave (sin “popout”, solo limpia el banner)
    window.clearTimeout(showMsg._t);
    showMsg._t = window.setTimeout(() => setUiMsg(null), 6000);
  };

  // WhatsApp y acciones rápidas: se gestionan desde el Detalle del proveedor.

  const loadProveedores = async () => {
    if (!profile?.organization_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('proveedores')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setProveedores(data || []);
    } catch (e) {
      console.error(e);
      showMsg('error', 'Error', 'No se pudieron cargar los proveedores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.organization_id) loadProveedores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const proveedoresFiltrados = useMemo(() => {
    const q = (busqueda || '').toLowerCase().trim();
    return (proveedores || [])
      .filter((p) => (verArchivados ? true : p?.is_active !== false))
      .filter((p) => {
        if (!q) return true;
        const rs = String(p?.razon_social || '').toLowerCase();
        const cuit = String(p?.cuit || '').toLowerCase();
        const mail = String(p?.email || '').toLowerCase();
        const tel = String(p?.telefono || '').toLowerCase();
        const dir = String(p?.direccion || '').toLowerCase();
        return rs.includes(q) || cuit.includes(q) || mail.includes(q) || tel.includes(q) || dir.includes(q);
      });
  }, [proveedores, busqueda, verArchivados]);

  const kpis = useMemo(() => {
    const activos = (proveedores || []).filter((p) => p?.is_active !== false).length;
    const archivados = (proveedores || []).filter((p) => p?.is_active === false).length;
    const saldoTotal = (proveedores || []).filter((p) => p?.is_active !== false).reduce((acc, p) => acc + Number(p?.saldo || 0), 0);
    return { activos, archivados, saldoTotal };
  }, [proveedores]);

  // Nota: el archivado/reactivado se gestiona desde el detalle (no desde el listado).

  return (
    <div className="w-full min-h-[calc(100vh-24px)] bg-[#F8FAFC] p-2 md:p-4 font-sans text-slate-700">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md">
            <Truck size={20} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Proveedores</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cartera y saldos</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-onboarding-id="proveedor-new-btn"
            onClick={() => onNavigate('proveedores-nuevo')}
            className="px-4 py-2.5 bg-emerald-500 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Plus size={16} /> Nuevo proveedor
          </button>
        </div>
      </div>

      {/* Mensajes inline */}
      {uiMsg && (
        <div className="mb-3">
          {uiMsg && (
            <div
              className={`rounded-2xl border p-3 shadow-sm flex items-start justify-between gap-3 ${
                uiMsg.type === 'error'
                  ? 'bg-rose-50 border-rose-200 text-rose-700'
                  : uiMsg.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}
            >
              <div className="flex items-start gap-2">
                {uiMsg.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest">{uiMsg.title}</div>
                  <div className="text-[11px] font-bold">{uiMsg.message}</div>
                </div>
              </div>
              <button
                onClick={() => setUiMsg(null)}
                className="p-2 rounded-xl hover:bg-white/40 transition"
                title="Cerrar"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* KPIs (mismo estilo que Clientes) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-3">
        {/* Activos (Emerald) */}
        <div className="bg-white px-3.5 py-3 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-300/30 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Activos</p>
            <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 group-hover:scale-110 transition-transform">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <p className="mt-1 text-xl font-black text-slate-800 tracking-tight">{kpis.activos}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Proveedores activos</p>
        </div>

        {/* Saldo Pendiente (Rose) */}
        <div className="bg-white px-3.5 py-3 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-rose-300/30 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Saldo Pendiente</p>
            <div className="p-1.5 rounded-lg bg-rose-50 text-rose-600 group-hover:scale-110 transition-transform">
              <Truck size={16} />
            </div>
          </div>
          <p className="mt-1 text-xl font-black text-rose-600 tracking-tight">$ {fmtMoney(kpis.saldoTotal)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Acumulado</p>
        </div>

        {/* Archivados (Slate/Amber) */}
        <div className="bg-white px-3.5 py-3 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Archivados</p>
            <div className="p-1.5 rounded-lg bg-slate-50 text-slate-500 group-hover:scale-110 transition-transform">
              <X size={16} />
            </div>
          </div>
          <p className="mt-1 text-xl font-black text-slate-800 tracking-tight">{kpis.archivados}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En historial</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-3.5 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-8">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Search size={12} /> Buscar
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] focus:bg-white transition-all shadow-inner"
                placeholder="Razón social, CUIT, email, teléfono..."
              />
            </div>
          </div>

          <div className="md:col-span-4 flex items-end">
            <button
              onClick={() => setVerArchivados((v) => !v)}
              className={`w-full px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                verArchivados
                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                  : 'bg-slate-50 text-slate-500 border-slate-100 hover:text-slate-700'
              }`}
            >
              {verArchivados ? 'Viendo Archivados' : 'Ver Archivados'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#0B2A36] px-5 py-2.5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">Listado ({proveedoresFiltrados.length})</span>
          </div>
        </div>

        {loading ? (
          <div className="p-7 text-center text-slate-400">
            <div className="inline-flex items-center gap-2">
              <Truck className="animate-pulse" size={16} />
              <span className="text-[10px] font-black uppercase tracking-widest">Cargando...</span>
            </div>
          </div>
        ) : proveedoresFiltrados.length === 0 ? (
          <div className="p-7 text-center text-slate-300">
            <div className="text-[10px] font-black uppercase tracking-widest">Sin resultados</div>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-400">
                <tr>
                  <th className="px-5 py-2.5">Proveedor</th>
                  <th className="px-4 py-2.5">CUIT</th>
                  <th className="px-4 py-2.5">Teléfono</th>
                  <th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5 text-right">Saldo</th>
                  <th className="px-4 py-2.5 text-center">Estado</th>
                  {/* Sin acciones inline: para ver/editar, hacer click en la fila */}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {proveedoresFiltrados.map((p) => {
                  const activo = p?.is_active !== false;
                  return (
                    <tr
                      key={p.id}
                      className="hover:bg-slate-50 transition-all cursor-pointer"
                      onClick={() => onNavigate(`proveedores-detalle/${p.id}`)}
                      title="Ver detalle"
                    >
                      <td className="px-5 py-2.5">
                        <div className="text-[11px] font-black text-slate-800 uppercase">{p.razon_social || 'PROVEEDOR'}</div>
                        <div className="text-[9px] font-bold text-slate-400">{p.tax_condition || '—'}</div>
                      </td>
                      <td className="px-4 py-2.5 text-[10px] font-bold text-slate-600">{p.cuit || '-'}</td>
                      <td className="px-4 py-2.5 text-[10px] font-bold text-slate-600">{p.telefono || '-'}</td>
                      <td className="px-4 py-2.5 text-[10px] font-bold text-slate-600">{p.email || '-'}</td>
                      <td className="px-4 py-2.5 text-right text-[11px] font-black text-slate-900">$ {fmtMoney(p.saldo)}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span
                          className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                            activo
                              ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                              : 'bg-slate-100 text-slate-500 border-slate-200'
                          }`}
                        >
                          {activo ? 'ACTIVO' : 'ARCHIVADO'}
                        </span>
                      </td>
                      {/* Acciones eliminadas en listado (estilo Clientes): usar detalle */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3 text-[9px] font-bold text-slate-400 text-center">{BRAND_NAME} ERP • Proveedores</div>
    </div>
  );
}
